import { useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
  Stack, Skeleton, Paper, alpha, useTheme, useMediaQuery, Divider, Tooltip,
  TablePagination,
} from '@mui/material';
import {
  MoreVert, Edit, Delete, Block, LockOpen, ToggleOn, ToggleOff, VerifiedUser, Inbox, Visibility,
} from '@mui/icons-material';
import { USER_TYPE_LABELS, resolveBlockedReason, type UserRow } from '../../constants/userManagement';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';

interface UserListTableProps {
  rows: UserRow[];
  loading?: boolean;
  onView?: (row: UserRow) => void;
  onEdit: (row: UserRow) => void;
  onDelete: (row: UserRow) => void;
  onToggleActive: (row: UserRow) => void;
  onBlock: (row: UserRow) => void;
  onUnblock: (row: UserRow) => void;
  totalCount?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function canViewUser(row: UserRow) {
  return row.userType === 'customer'
    || ['driver', 'delivery_partner', 'service_provider', 'merchant', 'vendor'].includes(row.userType || '');
}

function blockedReasonText(row: UserRow): string {
  return resolveBlockedReason(row);
}

function UserStatusChips({ row }: { row: UserRow }) {
  return (
    <Box>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          label={USER_TYPE_LABELS[row.userType || ''] || row.userType || '—'}
          variant="outlined"
          sx={{ textTransform: 'capitalize' }}
        />
        <Chip
          size="small"
          label={row.isActive ? 'Active' : 'Inactive'}
          color={row.isActive ? 'success' : 'default'}
        />
        {row.isBlocked && (
          <Tooltip title={blockedReasonText(row)}>
            <Chip size="small" label="Blocked" color="error" />
          </Tooltip>
        )}
        {!row.isVerified && <Chip size="small" label="Unverified" color="warning" variant="outlined" />}
      </Stack>
      {row.isBlocked && (
        <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.35, lineHeight: 1.35 }}>
          Reason: {blockedReasonText(row)}
        </Typography>
      )}
    </Box>
  );
}

function UserActionsMenu({
  row,
  anchorEl,
  onClose,
  onView,
  onEdit,
  onDelete,
  onToggleActive,
  onBlock,
  onUnblock,
}: {
  row: UserRow | null;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onView?: (row: UserRow) => void;
  onEdit: (row: UserRow) => void;
  onDelete: (row: UserRow) => void;
  onToggleActive: (row: UserRow) => void;
  onBlock: (row: UserRow) => void;
  onUnblock: (row: UserRow) => void;
}) {
  const canView = row && (row.userType === 'customer' || ['driver', 'delivery_partner', 'service_provider', 'merchant', 'vendor'].includes(row.userType || ''));
  return (
    <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={onClose}>
      {canView && onView && (
        <MenuItem onClick={() => { row && onView(row); onClose(); }}>
          <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
      )}
      <MenuItem onClick={() => { row && onEdit(row); onClose(); }}>
        <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
        <ListItemText>Edit</ListItemText>
      </MenuItem>
      <MenuItem onClick={() => { row && onToggleActive(row); onClose(); }}>
        <ListItemIcon>
          {row?.isActive ? <ToggleOff fontSize="small" /> : <ToggleOn fontSize="small" />}
        </ListItemIcon>
        <ListItemText>{row?.isActive ? 'Deactivate' : 'Activate'}</ListItemText>
      </MenuItem>
      {row?.isBlocked ? (
        <MenuItem onClick={() => { row && onUnblock(row); onClose(); }}>
          <ListItemIcon><LockOpen fontSize="small" /></ListItemIcon>
          <ListItemText>Unblock</ListItemText>
        </MenuItem>
      ) : (
        <MenuItem onClick={() => { row && onBlock(row); onClose(); }}>
          <ListItemIcon><Block fontSize="small" /></ListItemIcon>
          <ListItemText>Block</ListItemText>
        </MenuItem>
      )}
      <MenuItem onClick={() => { row && onDelete(row); onClose(); }} sx={{ color: 'error.main' }}>
        <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
        <ListItemText>Delete</ListItemText>
      </MenuItem>
    </Menu>
  );
}

function MobileUserCard({
  row,
  serialNo,
  onOpenMenu,
  onEdit,
}: {
  row: UserRow;
  serialNo: number;
  onOpenMenu: (e: React.MouseEvent<HTMLElement>, row: UserRow) => void;
  onEdit: (row: UserRow) => void;
}) {
  const name = `${row.firstName || ''} ${row.lastName || ''}`.trim() || '—';

  return (
    <Paper
      elevation={0}
      onClick={() => onEdit(row)}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        cursor: 'pointer',
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography variant="caption" fontWeight={800} color="text.secondary">{serialNo}.</Typography>
            <Typography variant="body2" fontWeight={700} noWrap>{name}</Typography>
            {row.isVerified && <VerifiedUser color="success" sx={{ fontSize: 16, flexShrink: 0 }} />}
          </Stack>
          {row.email && (
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {row.email}
            </Typography>
          )}
          {row.phone && (
            <Typography variant="caption" color="text.secondary" display="block">
              {row.phone}
            </Typography>
          )}
          <Box sx={{ mt: 1 }}>
            <UserStatusChips row={row} />
          </Box>
          {row.lastLogin && (
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.75 }}>
              Last login: {formatDateTime(row.lastLogin)}
            </Typography>
          )}
          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.35 }}>
            Registered: {formatDateTime(row.createdAt)}
          </Typography>
          {row.isBlocked && (
            <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5, whiteSpace: 'normal' }}>
              Blocked reason: {blockedReasonText(row)}
            </Typography>
          )}
        </Box>
        <Tooltip title="Actions">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onOpenMenu(e, row); }}>
            <MoreVert fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

export default function UserListTable({
  rows,
  loading,
  onView,
  onEdit,
  onDelete,
  onToggleActive,
  onBlock,
  onUnblock,
  totalCount,
  page = 0,
  pageSize = 25,
  onPageChange,
  onPageSizeChange,
}: UserListTableProps) {
  const theme = useTheme();
  const { t } = useI18n();
  const tl = (s: string) => translateUiText(s, t);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const serialOffset = page * pageSize;
  const showPagination = totalCount != null && onPageChange && onPageSizeChange;
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<UserRow | null>(null);

  const openMenu = (e: React.MouseEvent<HTMLElement>, row: UserRow) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  if (loading) {
    return (
      <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider' }}>
        {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={isMobile ? 88 : 52} sx={{ mx: 2, my: 0.5 }} />)}
      </Paper>
    );
  }

  if (!rows.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: 1,
          borderColor: 'divider',
          borderStyle: 'dashed',
          py: 6,
          textAlign: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Inbox sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="subtitle1" fontWeight={700} color="text.secondary">No users found</Typography>
        <Typography variant="body2" color="text.disabled">Try adjusting filters or add a new user</Typography>
      </Paper>
    );
  }

  if (isMobile) {
    return (
      <>
        <Stack spacing={1}>
          {rows.map((row, index) => (
            <MobileUserCard key={row._id} row={row} serialNo={serialOffset + index + 1} onOpenMenu={openMenu} onEdit={onEdit} />
          ))}
        </Stack>
        {showPagination && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, p) => onPageChange(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 25, 50]}
          />
        )}
        <UserActionsMenu
          row={menuRow}
          anchorEl={menuAnchor}
          onClose={closeMenu}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onToggleActive={onToggleActive}
          onBlock={onBlock}
          onUnblock={onUnblock}
        />
      </>
    );
  }

  return (
    <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', width: 56 }}>{tl('S.No')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>{tl('User')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', display: { md: 'none', lg: 'table-cell' } }}>{tl('Email/Phone Number')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>{tl('Type & Status')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', minWidth: 160 }}>{tl('Blocked Reason')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>{tl('Registered')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', display: { xs: 'none', lg: 'table-cell' } }}>{tl('Last Login')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', width: 56 }} align="right">{tl('Actions')}</TableCell>            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const name = `${row.firstName || ''} ${row.lastName || ''}`.trim() || '—';
              return (
                <TableRow
                  key={row._id}
                  hover
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}
                  onClick={() => onEdit(row)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color="text.secondary">{serialOffset + index + 1}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                      >
                        {name}
                      </Typography>
                      {row.isVerified && <VerifiedUser color="success" sx={{ fontSize: 16 }} />}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: { lg: 'none' } }}>
                      {row.email || row.phone || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { md: 'none', lg: 'table-cell' } }}>
                    <Typography variant="caption" display="block">{row.email || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">{row.phone || '—'}</Typography>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <UserStatusChips row={row} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 240, verticalAlign: 'top' }}>
                    {row.isBlocked ? (
                      <Typography variant="body2" color="error.main" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {blockedReasonText(row)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDateTime(row.createdAt)}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Typography variant="caption" display="block">
                      {formatDateTime(row.lastLogin)}
                    </Typography>
                    {row.lastLoginIp && (
                      <Typography variant="caption" color="text.secondary" display="block">{row.lastLoginIp}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" justifyContent="flex-end" spacing={0.25}>
                      {onView && canViewUser(row) && (
                        <Tooltip title="View">
                          <IconButton size="small" color="primary" onClick={() => onView(row)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {row.isBlocked ? (
                        <Tooltip title="Unblock">
                          <IconButton size="small" color="success" onClick={() => onUnblock(row)}>
                            <LockOpen fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Block">
                          <IconButton size="small" color="warning" onClick={() => onBlock(row)}>
                            <Block fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Actions">
                        <IconButton size="small" onClick={(e) => openMenu(e, row)}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider />

      {showPagination && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, p) => onPageChange(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50]}
        />
      )}

      <UserActionsMenu
        row={menuRow}
        anchorEl={menuAnchor}
        onClose={closeMenu}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
        onToggleActive={onToggleActive}
        onBlock={onBlock}
        onUnblock={onUnblock}
      />
    </Paper>
  );
}
